import React, { useEffect, useMemo, useState } from 'react'
import { Player } from '@lottiefiles/react-lottie-player'
import { css, keyframes } from '@emotion/core'
import styled from '@emotion/styled'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'

interface AiAvatarProps {
  listening: boolean
  size?: number
  variant?: 'aura' | 'icon'
}

// Outlined artwork from the same Lottie asset, cropped as in mobile's AiAvatarIcon.
const LETTER_A =
  'M251.765 74.862H117.605L92.404 0H12.848L142.807 364.182H228.787L357.758 0H275.236Z ' +
  'M230.517 137.618L185.056 280.919L138.112 137.618Z'
const LETTER_I = 'M398.03 364.182H473.634V0H398.03Z'
const SPARKLE =
  'M0-43.89C0-12.54 12.54 0 43.89 0C12.54 0 0 12.54 0 43.89C0 12.54-12.54 0-43.89 0C-12.54 0 0-12.54 0-43.89Z'

const iconPulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.12); }
`

const AvatarIcon = styled.svg<{ pulsing: boolean }>`
  display: block;
  flex-shrink: 0;
  overflow: visible;
  direction: ltr;
  transform-origin: center;
  animation: ${({ pulsing }) =>
    pulsing
      ? css`
          ${iconPulse} 1400ms ease-in-out infinite
        `
      : 'none'};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const parseColorNorm = (color: string) => {
  const rgba = color.match(/rgba?\(\s*(\d+),\s*(\d+),\s*(\d+)/)
  if (rgba) {
    return [parseInt(rgba[1]) / 255, parseInt(rgba[2]) / 255, parseInt(rgba[3]) / 255]
  }
  const h = color.replace('#', '')
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h
  return [
    parseInt(full.slice(0, 2), 16) / 255,
    parseInt(full.slice(2, 4), 16) / 255,
    parseInt(full.slice(4, 6), 16) / 255,
  ]
}

const lighten = (c: number, amt: number) => c + (1 - c) * amt

const tintAura = (raw: any, elementsColor: string): any => {
  const data = JSON.parse(JSON.stringify(raw))
  const [r, g, b] = parseColorNorm(elementsColor)

  const aura = data.layers?.find((l: any) => l.nm === 'Aura Glow')
  const gf = aura?.shapes?.[0]?.it?.find((it: any) => it.ty === 'gf')
  if (!gf) return data

  const stops: number = gf.g.p
  const k: number[] = gf.g.k.k
  for (let i = 0; i < stops; i++) {
    const off = i * 4
    const mix = i === 0 ? 0.15 : 0
    k[off + 1] = lighten(r, mix)
    k[off + 2] = lighten(g, mix)
    k[off + 3] = lighten(b, mix)
  }
  return data
}

const AuraAvatar = ({ listening, size }: { listening: boolean; size: number }) => {
  const elementsColor = useSelector((state: RootState) => state.general.theme?.elementsColor || '#FF2D78')
  const [raw, setRaw] = useState<any>(null)

  useEffect(() => {
    let cancelled = false
    fetch('/animations/ai-avatar.json')
      .then((res) => res.json())
      .then((json) => !cancelled && setRaw(json))
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])

  const data = useMemo(() => (raw && elementsColor ? tintAura(raw, elementsColor) : raw), [raw, elementsColor])

  if (!data) return null

  return (
    <Player
      autoplay={listening}
      loop
      src={data}
      // direction: ltr prevents RTL layout from mirroring the Lottie SVG
      style={{ width: size, height: size, overflow: 'visible', direction: 'ltr' } as React.CSSProperties}
    />
  )
}

export const AiAvatar = ({ listening, size = 220, variant = 'aura' }: AiAvatarProps) => {
  if (variant === 'icon') {
    return (
      <AvatarIcon
        width={size}
        height={size}
        viewBox="249 249 902 902"
        aria-hidden="true"
        focusable="false"
        pulsing={listening}
      >
        <circle cx={700} cy={700} r={451} fill="#000000" />
        <path d={SPARKLE} transform="translate(395.3 604.3)" fill="#FFFFFF" />
        <g transform="translate(447 862.8) scale(1 -1)" fill="#FFFFFF">
          <path d={LETTER_A} />
          <path d={LETTER_I} />
        </g>
      </AvatarIcon>
    )
  }

  return <AuraAvatar listening={listening} size={size} />
}
